import { useId, useRef, useEffect, useState } from 'react'

interface SegmentOption {
  value: string
  label: string
  icon?: React.ReactNode
}

interface SegmentedControlProps {
  options: SegmentOption[]
  value: string
  onChange: (value: string) => void
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  disabled?: boolean
}

export function SegmentedControl({
  options,
  value,
  onChange,
  size = 'md',
  fullWidth = true,
  disabled = false,
}: SegmentedControlProps) {
  const id = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })

  const sizeClasses = {
    sm: 'h-10 text-sm/5',
    md: 'h-12 text-base/6',
    lg: 'h-14 text-lg/7',
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const selectedIndex = options.findIndex((opt) => opt.value === value)
    const buttons = container.querySelectorAll('button')
    const selectedButton = buttons[selectedIndex]

    if (selectedButton) {
      const containerRect = container.getBoundingClientRect()
      const buttonRect = selectedButton.getBoundingClientRect()
      setIndicatorStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
      })
    }
  }, [value, options])

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label="Fulfillment method"
      className={`
        relative flex items-center gap-1 p-1 
        bg-surface-tertiary rounded-2xl
        ${fullWidth ? 'w-full' : 'w-fit'}
        ${disabled ? 'opacity-50 pointer-events-none' : ''}
      `}
    >
      {/* Sliding indicator */}
      <div
        className="absolute top-1 bottom-1 bg-surface-elevated rounded-xl shadow-card transition-all duration-200 ease-out"
        style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
      />

      {options.map((option) => {
        const isSelected = option.value === value
        return (
          <button
            key={option.value}
            id={`${id}-${option.value}`}
            role="tab"
            aria-selected={isSelected}
            aria-controls={`${id}-panel-${option.value}`}
            onClick={() => onChange(option.value)}
            disabled={disabled}
            className={`
              relative z-10 flex-1 flex items-center justify-center gap-2
              ${sizeClasses[size]} px-4 rounded-xl
              font-medium transition-colors duration-200
              ${
                isSelected
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary'
              }
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
            `}
          >
            {option.icon && <span className="size-5">{option.icon}</span>}
            <span>{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}
